import { FDRStatus, generatePath } from '@cibo/core'
import { usePaginatedFields } from '@cibo/landmanager'
import { Loading, useAuth } from '@cibo/profile'
import {
  EnrollmentWindowClosedModal,
  ProgramIcon,
  useProgramEngagements,
  useProgramModel,
} from '@cibo/programs'
import { Markdown } from '@cibo/ui'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { LayoutSection } from 'components/ImpactLayout'
import { ProgramStats } from 'components/ProgramStats'
import { PageLink, SecondaryNavigation } from 'components/SecondaryNavigation'
import { PROGRAM_PATHS, PROGRAM_ROUTES } from 'navigation/types/routes'
import { ProgramOpportunity } from 'pages/ProgramOpportunity'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, Routes, useParams } from 'react-router-dom'
import { ProgramInfo } from '../../../../components/ProgramEngagementPlayer/ProgramInfo'
import { ProgramAbout } from '../../ProgramAbout'
import { EligibleFields, eligibleFieldsBaseQuery } from './EligibleFields'
import { Engagements } from './Engagements'
import { EnrolledFields } from './EnrolledFields'
import { EnrollmentWindowAlert } from './EnrollmentWindowAlert'

export const ProgramV2Detail = () => {
  const { t } = useTranslation('impact/pages/ProgramV2DetailLayout')
  const { persona, userId } = useAuth()
  const { programId } = useParams()
  const programModel = useProgramModel({ programId })

  const engagements = useProgramEngagements({
    programId: programId ? [programId] : undefined,
  })

  const baseQuery = useMemo(() => (!!programId ? eligibleFieldsBaseQuery(programId) : undefined), [
    programId,
  ])
  const eligible = usePaginatedFields(0, 1, baseQuery, { enabled: !!programId })
  const enrolled = usePaginatedFields(
    0,
    1,
    { fdrStatusAnyOf: [{ status: FDRStatus.enrolled, programId }] },
    { enabled: !!programId }
  )

  const [enrollmentClosedModalOpen, setEnrollmentClosedModalOpen] = useState(false)

  const secondaryTabs = useMemo(() => {
    const isClosed = programModel.data?.isClosedForEnrollment
    const isFuture = programModel.data?.isFuture

    const tabs: PageLink[] = []

    if (
      (!isClosed || !!enrolled.data?.numAvailable) &&
      programModel.data?.engageType === 'enrollment'
    ) {
      tabs.push({
        label: t('enrolledFields'),
        to: generatePath(PROGRAM_ROUTES.DETAIL, { programId }),
        exact: false,
      })
    }
    if (!isClosed || !!engagements.data?.numItems) {
      tabs.push({
        label: t('engagements', { context: programModel.data?.program.engageType }),
        to:
          programModel.data?.engageType === 'application'
            ? generatePath(PROGRAM_ROUTES.DETAIL, { programId })
            : generatePath(PROGRAM_ROUTES.ENGAGEMENTS, { programId }),
        exact: false,
      })
    }
    if (!isClosed || !isFuture || (isFuture && !!eligible.data?.numAvailable)) {
      tabs.push({
        label: t('eligibleFields'),
        to: generatePath(PROGRAM_ROUTES.FIELDS_ELIGIBLE, { programId }),
        exact: false,
        count: eligible.data?.numAvailable,
        countColor: 'info',
      })
    }

    if (tabs.length) {
      tabs.push({
        label: t('about'),
        to: generatePath(PROGRAM_ROUTES.ABOUT, { programId }),
      })
    }

    return tabs
  }, [programModel.data, eligible.data])

  if (!userId || !programId) {
    return null
  }

  if (
    !programModel.isFetched ||
    !engagements.isFetched ||
    !eligible.isFetched ||
    !enrolled.isFetched
  ) {
    return <Loading />
  }

  return (
    <>
      <LayoutSection>
        <Stack spacing={2}>
          <EnrollmentWindowAlert setEnrollmentClosedModalOpen={setEnrollmentClosedModalOpen} />
          <Stack direction="row" alignItems="center" spacing={1}>
            {!!programModel.data?.program && <ProgramIcon program={programModel.data.program} />}
            <Typography variant="h6" color="text.primary">
              {programModel.data?.program?.content.fields.title}
            </Typography>
          </Stack>
        </Stack>
        <Divider />
        <Stack spacing={6} direction={{ xs: 'column', md: 'row-reverse' }}>
          {!programModel.data?.isFuture && (
            <Box width={{ xs: undefined, md: 350 }}>
              <Stack spacing={3} flex={1}>
                {persona === 'grower' ? (
                  <ProgramOpportunity programId={programId} sx={{ alignSelf: 'stretch' }} />
                ) : (
                  <ProgramStats programId={programId} />
                )}
              </Stack>
            </Box>
          )}
          <Stack spacing={2} flex={1}>
            {!!secondaryTabs.length ? (
              <>
                <SecondaryNavigation
                  basePath={generatePath(PROGRAM_ROUTES.DETAIL, { programId })}
                  pageLinks={secondaryTabs}
                />
                {programModel.data?.engageType === 'enrollment' && (
                  <Routes>
                    <Route path={PROGRAM_PATHS.ENGAGEMENTS} element={<Engagements />} />
                    <Route path={PROGRAM_PATHS.ELIGIBLE} element={<EligibleFields />} />
                    <Route path={PROGRAM_PATHS.ABOUT} element={<ProgramAbout />} />
                    <Route index element={<EnrolledFields />} />
                  </Routes>
                )}
                {programModel.data?.engageType === 'application' && (
                  <Routes>
                    <Route path={PROGRAM_PATHS.ELIGIBLE} element={<EligibleFields />} />
                    <Route path={PROGRAM_PATHS.ABOUT} element={<ProgramAbout />} />
                    <Route index element={<Engagements />} />
                  </Routes>
                )}
              </>
            ) : (
              <ProgramInfo hideGetStartedButton />
            )}
          </Stack>
        </Stack>
      </LayoutSection>

      <EnrollmentWindowClosedModal
        modal={false}
        programId={programId}
        open={enrollmentClosedModalOpen}
        onClose={() => setEnrollmentClosedModalOpen(false)}
        onDeleteSuccess={() => setEnrollmentClosedModalOpen(false)}
      >
        <Markdown>
          {t('deleteEnrollmentDescriptionList', {
            programName: programModel.data?.program.content.fields.title,
            context: persona,
            count: engagements.data?.numItems,
          })}
        </Markdown>
      </EnrollmentWindowClosedModal>
    </>
  )
}
